import { z } from 'zod';
import { MOODS, SUBTITLE_PRESETS } from './settings.js';

/** Правила монтажа. Их же дублирует системный промпт, а валидатор применяет силой. */
export const EDL_RULES = {
  /** Врезка короче не читается, длиннее — теряется говорящий. */
  cutawayMinS: 2,
  cutawayMaxS: 5,
  /** Начало ролика и концовка остаются за говорящим. */
  headGuardS: 1.5,
  tailGuardS: 2,
  /** Минимум речи между двумя врезками. */
  cutawayGapS: 3,
  hookMinS: 1.5,
  hookMaxS: 3,
  hookMaxChars: 60,
  minSegmentS: 0.4,
  /** При удалении запинки отдельное целое слово («если», «не») может быть короче. */
  minWordSegmentS: 0.08,
  captionMaxChars: 2200,
  hashtagsMin: 5,
  hashtagsMax: 12,
  /** На сколько можно подвинуть врезку, чтобы не разрезать слово. */
  snapToleranceS: 0.4,
} as const;

export const SegmentSchema = z.object({
  asset_id: z.string().optional().describe('Основной исходник, обязателен в EDL 2'),
  src_in: z.number().describe('Начало сегмента в записи эксперта, секунды'),
  src_out: z.number().describe('Конец сегмента в записи эксперта, секунды'),
  reason: z.string().describe('Почему сегмент оставлен, одна фраза для отладки и правок'),
});

export const HookSchema = z.object({
  enabled: z.boolean(),
  text: z.string().describe('До 40 знаков, 1-2 строки, без эмодзи'),
  duration_s: z.number().describe('Сколько секунд висит плашка, обычно 1.5-3'),
  moved_segment: z
    .number()
    .int()
    .nullable()
    .describe('Индекс сегмента timeline, переставленного в начало как визуальный хук, или null'),
});

export const CutawaySchema = z.object({
  asset_id: z.string().describe('Идентификатор иллюстрации из каталога'),
  mode: z.enum(['full', 'pip']).describe('full — на весь кадр, pip — поверх говорящей головы'),
  tl_in: z.number().describe('Начало врезки на таймлайне готового ролика, секунды'),
  tl_out: z.number().describe('Конец врезки на таймлайне готового ролика, секунды'),
  src_in: z.number().describe('Начало фрагмента в файле иллюстрации, для фото 0'),
  src_out: z.number().describe('Конец фрагмента в файле иллюстрации, для фото равен длительности врезки'),
  motion: z.enum(['none', 'kenburns']).describe('kenburns — медленный зум; none — неподвижное изображение, подходит для читаемого интерфейса'),
  why: z.string().describe('Какую фразу эксперта иллюстрирует врезка, показывается пользователю'),
  placement: z.enum(['contextual', 'fallback', 'manual']).optional(),
  anchor: z.object({
    asset_id: z.string().optional(),
    speech_in: z.number(), speech_out: z.number(), scene_in: z.number(), scene_out: z.number(),
  }).optional(),
});

export const CorrectionSchema = z.object({
  from: z.string().describe('Как распознала расшифровка'),
  to: z.string().describe('Как должно быть написано'),
});

export const SubtitlesSchema = z.object({
  enabled: z.boolean(),
  preset: z.enum(SUBTITLE_PRESETS),
  corrections: z
    .array(CorrectionSchema)
    .describe('Исправления терминов, имён и брендов в расшифровке'),
});

export const MusicSchema = z.object({
  track_id: z.string().nullable().optional(),
  enabled: z.boolean(),
  mood: z.enum(MOODS),
  energy: z.number().describe('0..1, влияет на выбор трека и уровень подмешивания'),
});

export const CoverSchema = z.object({
  locked: z.boolean().optional(),
  frame_tl_s: z.number().describe('Момент таймлайна для стоп-кадра обложки, выразительное лицо'),
  title: z.string().describe('Заголовок на обложке, до 35 знаков'),
});

export const PublishSchema = z.object({
  caption: z.string().describe('Описание поста на языке речи'),
  hashtags: z.array(z.string()).describe('5-12 хэштегов без решётки'),
});

export const EdlSchema = z.object({
  excluded_illustration_ids: z.array(z.string()).optional(),
  version: z.union([z.literal(1), z.literal(2)]),
  sources: z.array(z.object({
    asset_id: z.string(), filename: z.string(), duration_s: z.number().positive(),
    transcript_id: z.string().optional(),
    words: z.array(z.object({ text: z.string(), start: z.number(), end: z.number(), confidence: z.number().optional(), language: z.string().optional() })),
  })).optional(),
  omissions: z.array(z.object({ asset_id: z.string(), src_in: z.number(), src_out: z.number(), reason: z.string() })).optional(),
  target_duration_s: z.number(),
  timeline: z.array(SegmentSchema).min(1),
  hook: HookSchema,
  cutaways: z.array(CutawaySchema),
  subtitles: SubtitlesSchema,
  music: MusicSchema,
  cover: CoverSchema,
  publish: PublishSchema,
  /** Conservative output after repeated editorial validation failures. */
  recovery: z.object({
    reason: z.enum(['validation_cycle', 'repair_limit']),
    notice: z.string(),
    duration_limit_exceeded: z.boolean(),
  }).optional(),
  /** Смысловые привязки во времени исходника; старые проекты обходятся без них. */
  editorial: z.object({
    thesis: z.string(),
    /** Explicitly discarded take tokens, including overlapping ASR timestamps. */
    excluded_words: z.array(z.object({ asset_id: z.string().optional(), src_in: z.number(), src_out: z.number(), text: z.string() })).optional(),
    beats: z.array(z.object({ asset_id: z.string().optional(), src_in: z.number(), src_out: z.number(), role: z.string() })),
    accents: z.array(z.object({ asset_id: z.string().optional(), src_in: z.number(), src_out: z.number(), reason: z.string() })),
    pause_after: z.array(z.number()),
    source_pauses: z.array(z.object({ asset_id: z.string(), at: z.number() })).optional(),
  }).optional(),
}).superRefine((edl, ctx) => {
  if (edl.version !== 2) return;
  if (!edl.sources?.length) ctx.addIssue({ code: 'custom', message: 'EDL 2 требует исходники', path: ['sources'] });
  const sources = new Map(edl.sources?.map(s => [s.asset_id, s]));
  if (sources.size !== edl.sources?.length) ctx.addIssue({ code: 'custom', message: 'Повторный исходник', path: ['sources'] });
  for (const [index, s] of edl.timeline.entries()) {
    const source = s.asset_id ? sources.get(s.asset_id) : undefined;
    if (!source || s.src_in < 0 || s.src_out <= s.src_in || s.src_out > source.duration_s + 0.001) {
      ctx.addIssue({ code: 'custom', message: 'Сегмент вне своего исходника', path: ['timeline', index] });
    }
  }
});

export type Edl = z.infer<typeof EdlSchema>;
export type EdlSegment = z.infer<typeof SegmentSchema>;
export type EdlCutaway = z.infer<typeof CutawaySchema>;

/**
 * JSON Schema для structured output Claude. Источник истины — схема выше,
 * поэтому схема и парсер не разъезжаются. zod 4 сам проставляет
 * additionalProperties: false и полный required, как требует строгий режим.
 */
export function edlJsonSchema(): Record<string, unknown> {
  // Only the renderer may choose/persist a concrete track and freeze a cover.
  const plannerSchema = EdlSchema.safeExtend({
    music: MusicSchema.omit({ track_id: true }),
    cover: CoverSchema.omit({ locked: true }),
  });
  const schema = z.toJSONSchema(plannerSchema, { target: 'draft-7' }) as Record<string, unknown>;
  delete (schema['properties'] as Record<string, unknown>)['excluded_illustration_ids'];
  delete schema['$schema'];
  return schema;
}
